// Simple print helper — opens a new window with an A4-style stylesheet and triggers print.
// All interpolated values are HTML-escaped to prevent XSS from user-controlled data
// (customer name, PO no, remarks, article codes, etc. all originate from user input).

import { getPrintCompany } from "@/config/company";

const BASE_CSS = `
  @page { size: A4; margin: 12mm 10mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: 'Helvetica Neue', Arial, sans-serif; color: #111; background: #fff; font-size: 11pt; }
  .doc { padding: 4mm; }

  /* --------------------------------------------------------------
     Feb 2026 · Company Print Header (Rule-46-compliant for Indian
     GST invoicing). Rendered on every printable document — Sales
     Order, Packing List, Delivery Challan, Tax Invoice — via the
     company profile in /src/config/company.js. Hidden from the
     dashboard because the print doc lives in its own window, and
     also wrapped in @media print so if this helper is ever inlined
     into the main app it still stays off-screen on-screen.
     -------------------------------------------------------------- */
  .print-header { border: 2px solid #111; border-bottom: none; padding: 10px 14px; display: grid; grid-template-columns: auto 1.4fr 1fr; gap: 14px; align-items: start; }
  .print-header .co-logo { display: block; max-height: 22mm; object-fit: contain; }
  .print-header .co-name { font-size: 16pt; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; margin: 0 0 2px; }
  .print-header .co-tagline { font-size: 8pt; color: #555; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 6px; }
  .print-header .co-address { font-size: 9pt; color: #222; line-height: 1.4; white-space: pre-line; }
  .print-header .co-contact { font-size: 8.5pt; color: #333; text-align: right; display: flex; flex-direction: column; gap: 2px; }
  .print-header .co-contact .row { display:flex; justify-content:flex-end; gap: 6px; }
  .print-header .co-contact .row .k { color:#666; text-transform: uppercase; letter-spacing: 1px; font-size: 7.5pt; }
  .print-header .co-tax { grid-column: 1 / -1; border-top: 1px dashed #666; margin-top: 6px; padding-top: 6px; display:grid; grid-template-columns: repeat(4, 1fr); gap: 4px 14px; font-size: 8.5pt; }
  .print-header .co-tax .cell .v { font-family: 'Courier New', monospace; font-weight: 600; }

  .brand { display:flex; align-items:flex-start; justify-content:space-between; border-top: 1px solid #111; border-bottom: 2px solid #111; padding: 6px 10px; margin-bottom: 10px; background: #f5f5f5; }
  .brand h1 { font-size: 11pt; margin: 0; letter-spacing: 3px; text-transform: uppercase; color:#333; }
  .brand .doc-title { text-align: right; }
  .brand .doc-title .kind { font-size: 8pt; color: #666; letter-spacing: 2px; text-transform: uppercase; }
  .brand .doc-title .no { font-size: 14pt; font-weight: 700; font-family: 'Courier New', monospace; }
  /* Feb 2026 · Document Date inline with Document No in the header
     brand strip (operator request: PS / EPL / DC / EDC prints must
     surface the document date next to the document number). Rendered
     as a lightweight separator-row underneath the No so long numbers
     and long dates never collide at narrow A4 widths. */
  .brand .doc-title .date-row { display:flex; justify-content:flex-end; gap: 6px; margin-top: 2px; font-size: 8pt; color: #666; letter-spacing: 1.5px; text-transform: uppercase; align-items: baseline; }
  .brand .doc-title .date-row .dk { font-size: 7.5pt; letter-spacing: 2px; color: #666; }
  .brand .doc-title .date-row .dv { font-size: 10pt; font-weight: 700; font-family: 'Courier New', monospace; color: #111; letter-spacing: 1px; }
  .meta { display:grid; grid-template-columns: repeat(4, 1fr); gap: 6px 20px; margin: 10px 0 14px; }
  .meta .cell .label { font-size: 8pt; color: #666; text-transform: uppercase; letter-spacing: 1.2px; }
  .meta .cell .val { font-size: 10pt; font-weight: 600; font-family: 'Courier New', monospace; }
  h3.section { font-size: 10pt; text-transform: uppercase; letter-spacing: 2px; color: #333; margin: 14px 0 4px; border-bottom: 1px solid #ccc; padding-bottom: 3px; }

  /* --------------------------------------------------------------
     Feb 2026 · Bill To / Ship To block. Rendered above the line-items
     table on Sales Orders, Invoices, DCs. Uses a dedicated two-column
     bordered layout instead of the compact meta grid so long addresses
     (multi-line street, GSTIN, contact person) get room to breathe.
     -------------------------------------------------------------- */
  .addr-blocks { display: grid; grid-template-columns: 1fr 1fr; gap: 0; margin: 12px 0 6px; border: 1px solid #444; }
  .addr-blocks .addr { padding: 8px 12px; border-right: 1px solid #444; min-height: 32mm; }
  .addr-blocks .addr:last-child { border-right: none; }
  .addr-blocks .addr .k-row { display:flex; justify-content:space-between; align-items:baseline; margin-bottom: 4px; padding-bottom: 3px; border-bottom: 1px dashed #999; }
  .addr-blocks .addr .k { font-size: 8pt; letter-spacing: 2px; text-transform: uppercase; color: #555; font-weight: 700; }
  .addr-blocks .addr .tag { font-size: 7.5pt; letter-spacing: 1.5px; text-transform: uppercase; color: #666; background: #f0f0f0; padding: 1px 5px; border-radius: 2px; border: 1px solid #ddd; }
  .addr-blocks .addr .name { font-size: 10.5pt; font-weight: 700; color: #111; margin: 2px 0 4px; letter-spacing: 0.3px; }
  .addr-blocks .addr .street { font-size: 9pt; color: #222; line-height: 1.45; white-space: pre-line; margin-bottom: 3px; }
  .addr-blocks .addr .locale { font-size: 9pt; color: #222; margin-bottom: 3px; }
  .addr-blocks .addr .meta-row { font-size: 8.5pt; color: #333; margin-top: 4px; padding-top: 4px; border-top: 1px dotted #ccc; display: grid; grid-template-columns: auto 1fr; gap: 2px 8px; }
  .addr-blocks .addr .meta-row .mk { color:#666; text-transform:uppercase; font-size: 7.5pt; letter-spacing: 1px; }
  .addr-blocks .addr .meta-row .mv { font-family: 'Courier New', monospace; font-weight: 600; }
  table { width:100%; border-collapse: collapse; font-size: 9pt; }
  th, td { border: 1px solid #999; padding: 4px 6px; }
  th { background: #eee; text-transform: uppercase; letter-spacing: 1px; font-size: 8pt; }
  td.mono { font-family: 'Courier New', monospace; }
  td.right, th.right { text-align: right; }
  tfoot td { font-weight: 700; background: #f7f7f7; }
  /* Mar 2026 · Sum-Total-once fix. Any legacy tfoot markup rendered
     by future callers should NOT repeat per A4 page — neutralise the
     default table-footer-group display so it flows as normal rows and
     appears exactly once at the end of the table. */
  tfoot { display: table-row-group; }
  tr.total-row td { font-weight: 700; background: #f7f7f7; }
  /* Mar 2026 · DC/EDC signature polish — 4 columns fit "Prepared By /
     Driver / Receiver / Authorised By" horizontally without wrapping.
     Extra vertical margin gives operators room to physically sign.
     The --sig-cols var (set by caller) can override to 3 for legacy
     docs (SO / PO / Invoice); default is 4. */
  .signatures { display:grid; grid-template-columns: repeat(var(--sig-cols, 4), 1fr); gap: 24px; margin-top: 56px; font-size: 9pt; font-family: 'Helvetica Neue', 'Arial', sans-serif; }
  .signatures .sig { text-align:center; }
  .signatures .sig .name { font-size: 9pt; font-weight: 600; color: #111; margin-bottom: 4px; letter-spacing: 0.2px; font-style: italic; min-height: 1em; }
  .signatures .line { border-top: 1px solid #333; margin-top: 56px; padding-top: 6px; font-weight: 500; letter-spacing: 0.4px; }
  .signatures .ts { font-family: 'Courier New', monospace; font-size: 7.5pt; color: #666; margin-top: 3px; letter-spacing: 0.3px; }
  .footer-note { margin-top: 24px; font-size: 8pt; color: #888; text-align: center; }
  @media print {
    .no-print { display:none; }
    /* Print-only header safety net — keeps the company block visible on
       the printed page even if a future refactor ever inlines this helper
       into an on-screen dashboard component. Currently redundant (print
       runs in its own window) but future-proofs the requirement. */
    .print-header { display: grid !important; }
  }
  @media screen {
    /* Nothing to hide today — the print doc IS the whole window — but if
       any host ever renders the header inline, keep it off the dashboard. */
    .print-header.hide-on-screen { display: none; }
  }
`;

// Escape HTML entities to prevent XSS from user-controlled data.
function esc(v) {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderTable(section) {
  const cols = section.columns || [];
  const rows = section.rows || [];
  const th = cols.map(c => `<th class="${c.align === "right" ? "right" : ""}">${esc(c.label)}</th>`).join("");
  const body = rows.map(r =>
    `<tr>${cols.map(c => {
      const raw = r[c.key];
      const val = c.fmt ? c.fmt(raw, r) : (raw ?? "");
      return `<td class="${c.mono ? "mono " : ""}${c.align === "right" ? "right" : ""}">${esc(val)}</td>`;
    }).join("")}</tr>`
  ).join("");
  const foot = section.footerRow
    ? `<tfoot><tr>${cols.map(c => `<td class="${c.mono ? "mono " : ""}${c.align === "right" ? "right" : ""}">${esc(section.footerRow[c.key] ?? "")}</td>`).join("")}</tr></tfoot>`
    : "";
  return `${section.heading ? `<h3 class="section">${esc(section.heading)}</h3>` : ""}
    <table><thead><tr>${th}</tr></thead><tbody>${body}</tbody>${foot}</table>`;
}

// Feb 2026 · Render the company profile (name / address / GST + tax
// details / phone / email) as an A4-friendly print header block. Each cell
// is included only when non-blank so unused fields (e.g. blank CIN or IEC
// on a proprietorship) don't leave "—" holes.
function renderCompanyHeader(co) {
  const addr = (co.address || "").trim();
  const contactRows = [
    co.phone && ["Phone", co.phone],
    co.email && ["Email", co.email],
    co.website && ["Web", co.website],
  ].filter(Boolean);
  const taxCells = [
    co.gstin && ["GSTIN", co.gstin],
    co.pan && ["PAN", co.pan],
    co.cin && ["CIN", co.cin],
    co.iec && ["IEC", co.iec],
    co.udyam && ["Udyam", co.udyam],
  ].filter(Boolean);
  // Feb 2026 · Logo cell renders as a leading <img> when `co.logo` is set.
  // Accepts data-URI or an absolute/public URL. Relative URLs (produced by
  // the Object-Storage upload endpoint, e.g. `/api/files/company/x.jpg`)
  // are resolved against REACT_APP_BACKEND_URL so the print WINDOW (which
  // has about:blank as its origin) can still fetch the bytes.
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const resolvedLogo = (() => {
    const v = co.logo || "";
    if (!v) return "";
    if (v.startsWith("data:") || v.startsWith("http://") || v.startsWith("https://")) return v;
    if (v.startsWith("/")) return `${backendUrl}${v}`;
    return v;
  })();
  const logoWidth = Number(co.logoWidth) || 44;
  const logoCell = resolvedLogo
    ? `<img class="co-logo" src="${esc(resolvedLogo)}" alt="${esc(co.name || "Logo")}" style="width:${logoWidth}mm; max-width:${logoWidth}mm;" />`
    : "";
  // When no logo, collapse the left column so the name block spans it.
  const gridOverride = co.logo ? "" : ` style="grid-template-columns: 1.4fr 1fr;"`;
  return `
    <div class="print-header"${gridOverride}>
      ${logoCell}
      <div class="co-left">
        <div class="co-name">${esc(co.name || "")}</div>
        ${co.tagline ? `<div class="co-tagline">${esc(co.tagline)}</div>` : ""}
        ${addr ? `<div class="co-address">${esc(addr)}</div>` : ""}
      </div>
      <div class="co-contact">
        ${contactRows.map(([k, v]) => `<div class="row"><span class="k">${esc(k)}</span><span>${esc(v)}</span></div>`).join("")}
      </div>
      ${taxCells.length ? `<div class="co-tax">${taxCells.map(([k, v]) => `<div class="cell"><div class="k">${esc(k)}</div><div class="v">${esc(v)}</div></div>`).join("")}</div>` : ""}
    </div>`;
}

// SIGN-01 · repeating "system-generated" footer (tfoot repeats on every printed page) — mirrors backend services/doc_signatory.py.
export const SYSTEM_FOOTER_TEXT = "This is a system-generated document.";
const SYSDOC_CSS = `.sysdoc-wrap{width:100%;border-collapse:collapse;border:0;margin:0}
.sysdoc-wrap>tbody>tr>td,.sysdoc-wrap>tfoot>tr>td{border:0!important;padding:0!important;background:none!important}
.sysdoc-foot{margin-top:6px;padding-top:3px;border-top:1px solid #bbb;text-align:center;font-size:8.5px;color:#555;font-family:Arial,sans-serif;letter-spacing:.2px}
.sysdoc-sign{page-break-inside:avoid;break-inside:avoid}
.sysdoc-sigimg{height:40px;margin-top:4px;display:flex;align-items:flex-end;justify-content:flex-end}
.sysdoc-sigimg img{max-height:40px;max-width:170px;object-fit:contain}`;
const wrapSystemFooter = (inner) => `<table class="sysdoc-wrap"><tbody><tr><td>${inner}</td></tr></tbody><tfoot><tr><td><div class="sysdoc-foot">${SYSTEM_FOOTER_TEXT}</div></td></tr></tfoot></table>`;

// SIGN-01 · Browser Print for PL / EPL / DC / EDC: opens the window synchronously (popup-safe),
// fetches the document's active authorised signatory, then renders.
export async function printSignedDocument(docType, docId, opts) {
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) return;
  let signatory = null;
  try {
    const { default: api } = await import("./api");
    signatory = (await api.get(`/doc-export/${docType}/${docId}/signatory/block`)).data;
  } catch (_) { signatory = null; }
  printDocument({ ...opts, win, signatory, systemFooter: true });
}


export function printDocument({ company, kind, no, date, meta = [], addressBlocks = [], sections = [], signatures = ["Prepared By", "Checked By", "Authorized"], footer = "", win: preWin = null, signatory = null, systemFooter = false }) {
  // Pull the live company profile at print time so an AdminSettings edit
  // is visible on the very next click without a page refresh. `company`
  // param remains supported for tests / overrides.
  const co = company || getPrintCompany();
  const win = preWin || window.open("", "_blank", "width=900,height=1100");
  if (!win) return;
  const metaHtml = meta.map(m => `<div class="cell"><div class="label">${esc(m.label)}</div><div class="val">${esc(m.value ?? "—")}</div></div>`).join("");
  // Feb 2026 · Structured Bill To / Ship To. Each block carries a title,
  // an optional address-label tag ("Bill To" / "Warehouse A"), the party
  // name, multi-line street, a locale line (city / state / pin), country
  // and a `metaRows: [[key,value], …]` list for GSTIN / phone / contact.
  const addrHtml = addressBlocks.length
    ? `<div class="addr-blocks">${addressBlocks.map(a => `
        <div class="addr">
          <div class="k-row">
            <div class="k">${esc(a.title || "")}</div>
            ${a.tag ? `<div class="tag">${esc(a.tag)}</div>` : ""}
          </div>
          ${a.name ? `<div class="name">${esc(a.name)}</div>` : ""}
          ${a.street ? `<div class="street">${esc(a.street)}</div>` : ""}
          ${a.locale ? `<div class="locale">${esc(a.locale)}</div>` : ""}
          ${a.country ? `<div class="locale">${esc(a.country)}</div>` : ""}
          ${(a.metaRows && a.metaRows.length)
            ? `<div class="meta-row">${a.metaRows.map(([k, v]) => `<span class="mk">${esc(k)}</span><span class="mv">${esc(v)}</span>`).join("")}</div>`
            : ""}
        </div>`).join("")}</div>`
    : "";
  const sectionsHtml = sections.map(renderTable).join("");
  const sigHtml = signatures.map((s, idx) => {
    // Mar 2026 · DC/EDC footer polish — signatures accept either a raw
    // string ("Prepared By") or an object { label, name, timestamp }.
    // `name` renders italicised above the signing line; `timestamp`
    // renders below it in a small mono font for legal/audit clarity
    // (e.g. "Authorised: 08 Feb 2026 · 14:32 IST").
    const label = typeof s === "string" ? s : (s?.label || "");
    const authSlot = !!signatory && idx === signatures.length - 1; // SIGN-01 · last slot = authorised block, signatory-driven only
    const isAuth = authSlot && signatory.name;
    const name = authSlot ? (isAuth ? `Authorised by: ${signatory.name}` : "") : (typeof s === "string" ? "" : (s?.name || ""));
    const ts = typeof s === "string" ? "" : (s?.timestamp || "");
    const img = isAuth && /^data:image\/(png|jpeg);base64,/.test(signatory.image || "") ? signatory.image : "";
    return `<div class="sig sysdoc-sign">`
      + (img ? `<div class="sysdoc-sigimg" style="justify-content:center"><img src="${img}" alt="Signature" /></div>` : "")
      + (name ? `<div class="name">${esc(name)}</div>` : "")
      + `<div class="line">${esc(label)}</div>`
      + (ts ? `<div class="ts">${esc(ts)}</div>` : "")
      + `</div>`;
  }).join("");
  const headerHtml = renderCompanyHeader(co);
  const body = `<div class="doc">
    ${headerHtml}
    <div class="brand">
      <div>
        <h1>${esc(kind)}</h1>
      </div>
      <div class="doc-title">
        <div class="kind">Document No</div>
        <div class="no">${esc(no)}</div>
        ${date ? `<div class="date-row"><span class="dk">Document Date</span><span class="dv">${esc(date)}</span></div>` : ""}
      </div>
    </div>
    <div class="meta">${metaHtml}</div>
    ${addrHtml}
    ${sectionsHtml}
    <div class="signatures">${sigHtml}</div>
    ${footer ? `<div class="footer-note">${esc(footer)}</div>` : ""}
  </div>`;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(kind)} — ${esc(no)}</title><style>${BASE_CSS}${systemFooter ? SYSDOC_CSS : ""}</style></head>
  <body>${systemFooter ? wrapSystemFooter(body) : body}
  <script>window.onload = () => { setTimeout(() => window.print(), 300); };</script>
  </body></html>`;
  // Serialize HTML via Blob + window.location (avoids the document.write XSS sink; all values above are escaped).
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  win.location.href = url;
  // Revoke after the window has had time to load the resource
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}
